import React, { FC, useEffect, useMemo, useState } from 'react';
import { isEditorActive, withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import { WithTranslation, withTranslation } from 'react-i18next';
import useExperienceEditorPlaceholder from '../../hooks/useExperienceEditorPlaceholder';
import { isEmptyResponse } from '../../utils/checkEmptyResponse';
import EmptyApiDataMessage from '../@shared/emptyApiDataMessage';
import ExperienceEditorComponentPlaceholder from '../@shared/experienceEditorComponentPlaceholder';
import Heading from '../@shared/heading';
import UnreleasedComponent from '../@shared/unreleasedComponent';
import CompatibleLoadersTabs from './components/compatibleLoadersTabs';
import { ContentHubCompatibleLoadersModel } from './models/contentHubCompatibleLoaders.model';
import { TCHCompatibleLoadersProps } from './types/contentHubCompatibleLoaders.types';
import { windowService, documentService } from '../../utils/window.utils';
import LoaderContent from './components/loaderContent';
import CompatibleLoaderShowMoreOrLess from './components/compatibleLoaderShowMoreOrLess';
import useShowProductComponentTitle from '../../hooks/useShowProductComponentTitle';
import './styles.scss';

const ProductCompatibleLoaderListing: FC<WithTranslation & TCHCompatibleLoadersProps> = (props) => {
  const model = useMemo(() => new ContentHubCompatibleLoadersModel(props), [props]);
  const { t, extraClassName = '' } = props;
  const apiResponse = isEmptyResponse(props);
  const apiDataLength = props?.fields?.apiData?.items?.length > 0;
  const isChildrenPopulated = props?.fields?.apiData?.items?.some((e) => e?.items?.length !== 0);
  const checkEmptiness = apiResponse && apiDataLength && isChildrenPopulated;
  const [selectedTab, setSelectedTab] = useState(0);
  const [showMoreLoaders, setShowMoreLoader] = useState(false);
  const isPlaceholderShown = useExperienceEditorPlaceholder(props);
  const hasHeaderValue = !!(model?.title?.value || model?.description?.value);
  const dictionaryTerms = [props.titleKeyCompatibleLoaders, props.descriptionKeyCompatibleLoaders];
  const showHeader = useShowProductComponentTitle(hasHeaderValue, dictionaryTerms);


  useEffect(() => setShowMoreLoader(false), [selectedTab]);

  // Component is unreleased
  if (model.isUnreleased) {
    return <UnreleasedComponent />;
  }

  //  star node opened directly in EE
  if (isPlaceholderShown) {
    return <ExperienceEditorComponentPlaceholder image={model.thumbnailEE} componentName={model.componentName} />;
  }

  // not opened from star node
  // data missing when navigating via from products node
  if (!checkEmptiness && isEditorActive()) {
    return <EmptyApiDataMessage componentName={model.componentName} />;
  }

  // No data and not in EE
  if (!checkEmptiness && !isPlaceholderShown && !isEditorActive()) {
    return null;
  }

  const selectedTabItems = model.tabs?.find((_, index: number) => index === selectedTab)?.items;
  const showMoreLoaderEvent = (showMoreLoaders) => {
    setShowMoreLoader(showMoreLoaders);
    if (!showMoreLoaders) {
      const element = documentService().getElementById(props.id);
      const headerOffset = 85;
      const elementPosition = element?.getBoundingClientRect().top;
      const offsetPosition = elementPosition ? elementPosition + windowService().pageYOffset - headerOffset : 0;

      windowService().scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  };

  return (
    <article className={`ch-compatible-loaders ${extraClassName}`} id={props.id}>
      {showHeader && (
        <header className={'ch-compatible-loaders__header'}>
          <Heading
            title={model.title}
            subtitle={model.description}
            editable={true}
            useDictionaryFallback
            titleDictionaryKey={props.titleKeyCompatibleLoaders}
            descriptionDictionaryKey={props.descriptionKeyCompatibleLoaders}
          />
        </header>
      )}

      {!props.hiddenTabs && model.tabs?.length > 1 && (
        <CompatibleLoadersTabs tabs={model.tabs} selectedTab={selectedTab} onSelect={setSelectedTab} />
      )}

      <div className={'ch-compatible-loaders__content-container'}>
        <LoaderContent
          baseUrl={model?.baseUrl}
          tabs={model.tabs}
          hiddenTabs={props?.hiddenTabs}
          selectedTab={selectedTab}
          showMoreLoaders={showMoreLoaders}
        />

        <CompatibleLoaderShowMoreOrLess
          hiddenTabs={props?.hiddenTabs}
          selectedTabItems={selectedTabItems}
          showMoreLoaders={showMoreLoaders}
          showMoreLoaderEvent={showMoreLoaderEvent}
          tabs={model.tabs}
          t={t}
        />
      </div>
    </article>
  );
};

export default withSitecoreContext()(withTranslation()(ProductCompatibleLoaderListing));
